/**
 * Magon — Gift Promotion (Compre X, Leve Y)
 *
 * Auto-add e auto-remove do produto-brinde conforme o cliente atinge ou
 * deixa de atingir o threshold configurado. O desconto que zera o preço
 * continua sendo o do Shopify (Discounts > Buy X Get Y) — este script só
 * cuida da UX (adicionar, remover, atualizar a barra de progresso).
 *
 * Regras (alinhadas com o cliente em 2026-05-25):
 *   #4  Cai abaixo do threshold → remove o brinde silenciosamente + toast
 *   #6  Cliente removeu manualmente → não re-adiciona na sessão atual,
 *       mas re-adiciona em nova sessão (sessionStorage)
 *   #7  Brinde com 2+ variantes → modal para escolher (sem variante padrão)
 *   #11 Tier único (não há array de tiers)
 */
(function () {
  if (customElements.get('magon-gift-promotion')) return;

  const SESSION_KEY = 'magon_gift_dismissed';

  class MagonGiftPromotion extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.thresholdCents, 10) || 0;
      this.giftProductId = parseInt(this.dataset.giftProductId, 10) || 0;
      this.giftVariantCount = parseInt(this.dataset.giftVariantCount, 10) || 0;
      this.lockedVariantId = parseInt(this.dataset.lockedVariantId, 10) || 0;

      const configEl = this.querySelector('[data-gift-config]');
      try {
        const cfg = JSON.parse(configEl ? configEl.textContent : '{}');
        this.qualifyingIds = Array.isArray(cfg.qualifyingProductIds)
          ? new Set(cfg.qualifyingProductIds)
          : null;
        this.variants = Array.isArray(cfg.giftVariants) ? cfg.giftVariants : [];
      } catch {
        this.qualifyingIds = null;
        this.variants = [];
      }

      this.fillEl = this.querySelector('[data-fill]');
      this.textEl = this.querySelector('[data-text]');
      this.modalEl = this.querySelector('[data-gift-modal]');

      this._wireModal();
      this._wireCartListener();
      this._wireManualRemoveListener();

      // Estado inicial baseado no cart atual
      this._sync();
    }

    /* -------------------------------- utils -------------------------------- */

    _money(cents) {
      const reais = (cents / 100).toFixed(2).replace('.', ',');
      return 'R$ ' + reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    _fetchCart() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json());
    }

    _dispatchCartUpdate(data) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'magon-gift',
            data: { source: 'magon-gift', sections: data && data.sections },
          },
        })
      );
    }

    _toast(msg) {
      let el = document.querySelector('.magon-gift-toast');
      if (!el) {
        el = document.createElement('div');
        el.className = 'magon-gift-toast';
        document.body.appendChild(el);
      }
      el.textContent = msg;
      requestAnimationFrame(() => el.classList.add('magon-gift-toast--visible'));
      clearTimeout(el._timer);
      el._timer = setTimeout(() => el.classList.remove('magon-gift-toast--visible'), 3000);
    }

    _sectionIds() {
      return Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
    }

    /* -------------------- cálculo do subtotal qualificado -------------------- */

    _qualifyingSubtotal(cart) {
      if (!this.qualifyingIds) {
        // Sem filtro de coleção: usa o subtotal direto, ignorando o brinde
        return cart.items.reduce((sum, item) => {
          if (item.product_id === this.giftProductId) return sum;
          return sum + item.final_line_price;
        }, 0);
      }
      return cart.items.reduce((sum, item) => {
        if (item.product_id === this.giftProductId) return sum;
        if (!this.qualifyingIds.has(item.product_id)) return sum;
        return sum + item.final_line_price;
      }, 0);
    }

    _giftLineInCart(cart) {
      return cart.items.find((i) => i.product_id === this.giftProductId);
    }

    /* ---------------------------- core de sync ---------------------------- */

    async _sync(skipMutations) {
      let cart;
      try {
        cart = await this._fetchCart();
      } catch {
        return;
      }

      const subtotal = this._qualifyingSubtotal(cart);
      const eligible = subtotal >= this.threshold;
      const giftLine = this._giftLineInCart(cart);
      const dismissed = sessionStorage.getItem(SESSION_KEY) === '1';

      // UI sempre atualiza (mesmo quando o evento foi disparado pelo próprio brinde —
      // o morph do drawer reverte o texto pro SSR e precisamos re-aplicar).
      this._updateBar(subtotal, eligible, !!giftLine);

      // Mutações (add/remove) só rodam quando o evento NÃO veio do próprio brinde,
      // pra evitar loop infinito.
      if (skipMutations) return;

      // Regra #4 — caiu abaixo: remove o brinde (silenciosamente)
      if (!eligible && giftLine) {
        await this._removeGift(giftLine.key, /*fromManual=*/ false);
        return;
      }

      if (eligible && !giftLine && !dismissed) {
        await this._addGift();
      }
    }

    _updateBar(subtotal, eligible, hasGift) {
      if (!this.fillEl || !this.textEl) return;
      const pct = this.threshold > 0
        ? Math.min(100, Math.round((subtotal * 100) / this.threshold))
        : 100;
      this.fillEl.style.width = pct + '%';
      this.fillEl.classList.toggle('magon-gift-promo__fill--complete', eligible);

      if (eligible) {
        this.textEl.innerHTML = '<strong>' + this._escape(this.dataset.textUnlocked) + '</strong>';
      } else {
        const missing = this.threshold - subtotal;
        const tpl = this.dataset.textProgress || 'Faltam [amount]';
        this.textEl.innerHTML = tpl.replace(
          '[amount]',
          '<strong>' + this._money(missing) + '</strong>'
        );
      }
    }

    _escape(s) {
      return String(s || '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      })[c]);
    }

    /* ----------------------------- add/remove ----------------------------- */

    async _addGift() {
      // Variante travada via setting (caso o desconto cubra apenas 1 variante)
      if (this.lockedVariantId) {
        const locked = this.variants.find((v) => v.id === this.lockedVariantId);
        if (locked && locked.available) return this._postAdd(locked.id);
        this._toast('Brinde temporariamente esgotado.');
        return;
      }

      const available = this.variants.filter((v) => v.available);
      if (available.length === 0) {
        this._toast('Brinde temporariamente esgotado.');
        return;
      }
      if (available.length === 1 || this.giftVariantCount <= 1) {
        return this._postAdd(available[0].id);
      }
      // 2+ variantes disponíveis → modal (regra #7)
      this._openModal();
    }

    async _postAdd(variantId) {
      const body = { id: parseInt(variantId, 10), quantity: 1 };
      const sids = this._sectionIds();
      if (sids.length) body.sections = sids.join(',');

      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        sessionStorage.removeItem(SESSION_KEY);
        this._toast(this.dataset.textUnlocked || 'Brinde adicionado!');
        this._dispatchCartUpdate(data);
      } catch {
        /* silencioso */
      }
    }

    async _removeGift(lineKey, fromManual) {
      try {
        const body = { id: lineKey, quantity: 0 };
        const sids = this._sectionIds();
        if (sids.length) body.sections = sids.join(',');
        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!fromManual) {
          const missing = this.threshold - this._qualifyingSubtotal(data);
          const tpl = this.dataset.textRemoved || 'Brinde removido';
          this._toast(tpl.replace('[amount]', this._money(Math.max(0, missing))));
        }
        this._dispatchCartUpdate(data);
      } catch {
        /* silencioso */
      }
    }

    /* ------------------------------ modal ------------------------------ */

    _wireModal() {
      if (!this.modalEl) return;
      this.modalEl.addEventListener('click', (e) => {
        const variantBtn = e.target.closest('[data-variant-id]');
        if (variantBtn && !variantBtn.disabled) {
          this._closeModal();
          this._postAdd(variantBtn.dataset.variantId);
          return;
        }
        if (e.target.closest('[data-gift-modal-close]')) {
          this._closeModal();
          // Cliente cancelou o modal — equivale a dispensar (regra #6)
          sessionStorage.setItem(SESSION_KEY, '1');
        }
      });
    }

    _openModal() {
      if (this.modalEl && typeof this.modalEl.showModal === 'function') {
        try { this.modalEl.showModal(); } catch { /* já aberto */ }
      }
    }

    _closeModal() {
      if (this.modalEl && typeof this.modalEl.close === 'function') {
        try { this.modalEl.close(); } catch {}
      }
    }

    /* --------------------------- event wiring --------------------------- */

    _wireCartListener() {
      this._onUpdate = (e) => {
        // Eventos do próprio brinde: só atualiza UI, não dispara add/remove (evita loop)
        const src = e.detail && e.detail.data && e.detail.data.source;
        this._sync(src === 'magon-gift');
      };
      document.addEventListener('cart:update', this._onUpdate);
    }

    /**
     * Regra #6: detecta quando o cliente clica em remover o brinde.
     * Faz delegation no document, capturando ANTES do request acontecer.
     */
    _wireManualRemoveListener() {
      const isGiftRemovalTarget = (el) => {
        if (!el) return false;
        const line = el.closest('[data-product-id], [data-line-item]');
        if (line && parseInt(line.dataset.productId, 10) === this.giftProductId) return true;
        const btn = el.closest('cart-remove-button, [data-cart-remove]');
        if (btn) {
          const vId = parseInt(btn.dataset.variantId || btn.dataset.id || '0', 10);
          if (vId && this.variants.some((v) => v.id === vId)) return true;
        }
        return false;
      };

      document.addEventListener(
        'click',
        (e) => {
          if (!isGiftRemovalTarget(e.target)) return;
          sessionStorage.setItem(SESSION_KEY, '1');
        },
        true
      );
    }

    disconnectedCallback() {
      if (this._onUpdate) document.removeEventListener('cart:update', this._onUpdate);
    }
  }

  customElements.define('magon-gift-promotion', MagonGiftPromotion);
})();
